import { Controller } from "@hotwired/stimulus"
import BaseController from './base_controller'

export default class extends BaseController {
  static targets = ["requiredInput", "fullInput", "ecoInput", "pair"]
  static values = {
    tbn: Boolean,
    // Per-mount switches for which validation clusters run (mobile, desktop, SnP differ).
    requireFullColumn: Boolean,
    requireGranular: Boolean,
    incompleteMessage: String,
    rangeSpeedMessage: String,
    rangeConsumptionMessage: String,
    differenceEcoMessage: String,
    differenceFullMessage: String,
    orderMessage: String
  }

  connect() {
    // Shared by paintColumn and the pair-based checks so a completeness border and a range/order/difference border on the same input don't clobber each other (see setError/refresh).
    this.errors = new WeakMap()

    // Submission gating applies only where a mount declares completeness targets; a display-only mount (e.g. a read-only vessel comparison) gets per-cell tooltips alone.
    if (this.hasRequiredInputTarget || this.hasFullInputTarget || this.hasEcoInputTarget) {
      this.boundSubmit = this.handleSubmit.bind(this)
      this.boundInput = this.handleInput.bind(this)
      this.element.addEventListener("submit", this.boundSubmit)
      this.element.addEventListener("input", this.boundInput)
      this.validateAndToggle()
    }

    if (this.requireGranularValue) {
      this.boundChange = this.handleChange.bind(this)
      this.element.addEventListener('change', this.boundChange)
    }

    // calculate_single.js.haml strips .field-error from every input after ajax:complete but leaves this controller's own flags/tooltips untouched, so repaint from them here.
    this.form = this.element.closest('form')
    if (this.form) {
      this.boundRevalidate = () => this.revalidate()
      this.form.addEventListener('ajax:complete', this.boundRevalidate)
    }
  }

  disconnect() {
    if (this.boundSubmit) {
      this.element.removeEventListener("submit", this.boundSubmit)
    }
    if (this.boundInput) {
      this.element.removeEventListener("input", this.boundInput)
    }
    if (this.boundChange) {
      this.element.removeEventListener('change', this.boundChange)
      this.inputs().forEach(input => this.disposeTooltip(input))
    }
    if (this.form && this.boundRevalidate) {
      this.form.removeEventListener('ajax:complete', this.boundRevalidate)
    }
  }

  handleSubmit(event) {
    if (this.hasMissing()) {
      event.preventDefault()
      event.stopPropagation()
      this.hideLoading()
      this.showMessage()
    }
  }

  handleInput() {
    this.validateAndToggle()
  }

  // Reports completeness via the "state" event only — the mounting form controller owns the submit button.
  validateAndToggle() {
    const missing = this.hasMissing()

    // Only a partially filled column gets flagged — a fully blank column is a valid choice.
    this.paintColumn(this.fullInputTargets, this.columnState(this.fullInputTargets) === 'partial')
    this.paintColumn(this.ecoInputTargets, this.columnState(this.ecoInputTargets) === 'partial')
    this.paintColumn(this.requiredInputTargets, this.requiredMissing())

    this.dispatch("state", {
      bubbles: true,
      detail: { missing }
    })
  }

  // Valid when every required input is filled and, where a Full/Eco split applies, at least one column is complete with neither partially filled.
  hasMissing() {
    if (this.tbnValue) return false
    return this.requiredMissing() || this.speedColumnsInvalid()
  }

  requiredMissing() {
    return this.requiredInputTargets.some(input => this.isEmpty(input))
  }

  // Only gates completeness when requireFullColumn is set; SnP leaves it off and requires every field via requiredInput instead.
  speedColumnsInvalid() {
    if (!this.requireFullColumnValue) return false
    const full = this.columnState(this.fullInputTargets)
    const eco = this.columnState(this.ecoInputTargets)
    return full === 'partial' || eco === 'partial' || (full === 'empty' && eco === 'empty')
  }

  columnState(inputs) {
    if (inputs.length === 0) return 'empty'
    const filled = inputs.filter(input => !this.isEmpty(input)).length
    if (filled === 0) return 'empty'
    if (filled === inputs.length) return 'complete'
    return 'partial'
  }

  paintColumn(inputs, flag) {
    inputs.forEach(input => {
      const shouldFlag = flag && this.isEmpty(input)
      // Routed through setError/refresh, not classList.toggle directly, so this doesn't clobber a range/order/difference border already set on the same input.
      this.setError(input, 'missing', shouldFlag)
      this.refresh(input)
    })
  }

  isEmpty(input) {
    return input.value === '' || input.value === null || input.value === undefined
  }

  // Repaints borders from already-recorded flags only — calculating never changes an input's value, so nothing here needs recomputing.
  revalidate() {
    const targets = new Set([
      ...this.fullInputTargets, ...this.ecoInputTargets, ...this.requiredInputTargets, ...this.inputs()
    ])
    targets.forEach(input => this.refresh(input))
  }

  showMessage() {
    this.dispatchNotify(this.incompleteMessageValue, { type: "error" })
  }

  // --- Per-cell range/order/difference validation (vessel form, SNP) ---

  inputs() {
    return Array.from(this.element.querySelectorAll('input[data-speed], input[data-consumption]'))
  }

  handleChange(event) {
    const input = event.target
    if (!(input instanceof HTMLInputElement)) return
    const isSpeed = 'speed' in input.dataset
    const isConsumption = 'consumption' in input.dataset
    if (!isSpeed && !isConsumption) return

    const affected = new Set([input])
    if (isSpeed) {
      this.computeDifference(input, affected)
      this.computeRange(input)
      this.computeOrder(input, input.dataset.speed, 'speed', affected)
    } else {
      this.computeRange(input)
      this.computeOrder(input, input.dataset.consumption, 'consumption', affected)
    }
    affected.forEach(el => this.refresh(el))
  }

  // value outside [min, max]. Absent min/max (TBN speeds, MGO rows) → NaN → no error.
  computeRange(input) {
    const max = parseFloat(input.dataset.max)
    const min = parseFloat(input.dataset.min)
    const value = parseFloat(input.value)
    this.setError(input, 'range', value > max || value < min)
  }

  // Full and eco speeds must differ by at least 0.5kn; a blank side (NaN) never flags, so single-column entries are tolerated.
  computeDifference(input, affected) {
    const opposite = this.opposite(input, 'speed')
    const tooClose = Math.abs(parseFloat(this.valueOf(opposite)) - parseFloat(input.value)) < 0.5
    this.setError(input, 'difference', tooClose)
    if (opposite) {
      this.setError(opposite, 'difference', tooClose)
      affected.add(opposite)
    }
  }

  // the higher (full) value must be entered left of the lower (eco) one.
  computeOrder(input, column, label, affected) {
    const opposite = this.opposite(input, label)
    const oppositeValue = parseFloat(this.valueOf(opposite))
    const value = parseFloat(input.value)
    const outOfOrder = (column === 'full' && oppositeValue > value) ||
      (column === 'eco' && oppositeValue < value)
    this.setError(input, 'order', outOfOrder)
    if (opposite) {
      this.setError(opposite, 'order', outOfOrder)
      affected.add(opposite)
    }
  }

  opposite(input, label) {
    const other = input.dataset[label] === 'full' ? 'eco' : 'full'
    const pair = this.pairTargets.find(cell => cell.contains(input))
    return pair ? pair.querySelector(`input[data-${label}="${other}"]`) : null
  }

  valueOf(input) {
    return input ? input.value : ''
  }

  errorsFor(input) {
    let flags = this.errors.get(input)
    if (!flags) {
      flags = {}
      this.errors.set(input, flags)
    }
    return flags
  }

  setError(input, kind, present) {
    const flags = this.errorsFor(input)
    if (present) flags[kind] = true
    else delete flags[kind]
  }

  refresh(input) {
    const flags = this.errorsFor(input)
    const hasError = !!(flags.range || flags.difference || flags.order || flags.missing)
    input.classList.toggle('field-error', hasError)
    // Where present, also paint the .form-input wrapper the calculator card styles instead.
    const wrapper = input.closest('.form-input')
    if (wrapper) wrapper.classList.toggle('field-error', hasError)
    const message = hasError ? this.messageFor(input, flags) : ''
    if (message) {
      addFormErrorTooltip($(input), message)
    } else {
      this.disposeTooltip(input)
    }
  }

  // Precedence: range → difference → order.
  messageFor(input, flags) {
    if (flags.range) {
      const template = 'speed' in input.dataset ? this.rangeSpeedMessageValue : this.rangeConsumptionMessageValue
      return template
        .replace('{max}', parseFloat(input.dataset.max).toFixed(2))
        .replace('{min}', parseFloat(input.dataset.min).toFixed(2))
    }
    if (flags.difference) {
      return input.dataset.speed === 'eco' ? this.differenceEcoMessageValue : this.differenceFullMessageValue
    }
    if (flags.order) return this.orderMessageValue
    return ''
  }

  disposeTooltip(input) {
    const tooltip = bootstrap.Tooltip.getInstance(input)
    if (tooltip) tooltip.dispose()
  }
}
